// Console theme (light / dark).
//
// LIGHT is the default. The console used to mirror the OS colour scheme, which
// meant anyone on a dark desktop got a dark console they never asked for. Now
// the console starts light and dark is an explicit, persisted opt-in.
//
// Dark is expressed as the `data-dark` attribute on <html> — the selector the
// design system ships its dark palette under (@solya/design-tokens), which the
// `--sol-*` bridge in styles/tokens.css then follows automatically.
//
// The same key is read by the inline bootstrap in index.html, before paint, so
// a stored preference does not flash the wrong theme on load. Keep them in sync.

import { useCallback, useEffect, useState } from "react"

export type ThemeMode = "light" | "dark"

export const THEME_STORAGE_KEY = "solya.console.theme"

/** The theme used when the user has expressed no preference. */
export const DEFAULT_THEME: ThemeMode = "light"

/**
 * The persisted preference, or the default when none is stored. Storage access
 * can throw (private mode, blocked cookies), so every read is guarded.
 */
export function readStoredTheme(): ThemeMode {
  try {
    return window.localStorage.getItem(THEME_STORAGE_KEY) === "dark" ? "dark" : DEFAULT_THEME
  } catch {
    return DEFAULT_THEME
  }
}

/** Reflect the mode onto <html data-dark> so the design tokens flip. */
export function applyTheme(mode: ThemeMode): void {
  document.documentElement.toggleAttribute("data-dark", mode === "dark")
}

/** Persist the preference. Best-effort: a storage failure must not break the UI. */
export function storeTheme(mode: ThemeMode): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, mode)
  } catch {
    /* storage unavailable — the theme still applies for this session */
  }
}

/** Current theme + a toggle that persists and applies the new mode. */
export function useTheme(): { theme: ThemeMode; toggle: () => void } {
  const [theme, setTheme] = useState<ThemeMode>(readStoredTheme)

  // Keep the DOM in sync with state — covers the first render too, so the
  // attribute is correct even if the inline bootstrap did not run (e.g. tests).
  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  const toggle = useCallback(() => {
    setTheme((prev) => {
      const next: ThemeMode = prev === "dark" ? "light" : "dark"
      storeTheme(next)
      return next
    })
  }, [])

  return { theme, toggle }
}
